import { HttpParams } from '@angular/common/http';
import { AfterContentInit, Component, ContentChild, ContentChildren, forwardRef, Inject, Input, OnInit, QueryList, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import * as _ from "lodash";
import { take } from 'rxjs';
import { Permissions } from '../../common/constants';
import { PropertyInfo } from '../../common/properties';
import { ActionExecutionState, ActionState, ActionStatus, ActivationType, Thing, WorkOrder, WorkOrderStatus } from '../../model';
import { AbstractExportContextService } from '../../service/abstract-export-context.service';
import { ActionContextService } from '../../service/action-context.service';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { FieldService } from '../../service/field.service';
import { NavigationService } from '../../service/navigation.service';
import { StatisticService } from '../../service/statistic.service';
import { ThingContextService } from '../../service/thing-context.service';
import { UserActionStatusService } from '../../service/user-action-status.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { ActionSnackbarButton, ActionSnackbarButtonType } from '../../shared/component/action-snackbar/action-snackbar.component';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ButtonActionValue, CustomTableColumn, CustomTableService } from '../../shared/custom-table';
import { ClickOnRowBehaviour, ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { DatetimeFormatterPipe, LocalizationPipe } from '../../shared/pipe';
import { ActionExecutionStatePipe } from '../../shared/pipe/action-execution-state.pipe';
import { SearchFieldComponent, SearchFieldInput, SearchFieldInputType } from '../../shared/search-field/search-field.component';
import { COMPONENT_DEFINITION_REF } from "../../shared/utility/component-definition-token";
import { ErrorUtility } from '../../utility/error-utility';
import { ActionInfoDialogV2Component } from '../action-info-dialog/action-info-dialog-v2.component';
import { ActionListWidgetReschedulingDialogComponent } from '../action-list/action-list-widget-rescheduling-dialog.component';
import { SnackbarService } from '../shared/snackbar.service';
import { ThingListWidgetV2Service } from '../thing-list/thing-list-widget-v2.service';
import { ActionListWidgetV2Service } from './action-list-widget-v2.service';
import { AddActionDialogComponent } from './add-action-dialog.component';
import { AddToWorkOrderDialogComponent } from './add-to-work-order-dialog.component';
import { DiscardActionDialogComponent } from './discard-action-dialog.component';
import { MarkAsDoneWithIssueActionDialogComponent } from './mark-as-done-with-issue-action-dialog.component';
import { WorkOrderEditDialogComponent } from './work-order-edit-dialog.component';
import { WorkOrderListDialogComponent } from './work-order-list-dialog.component';

@Component({
    selector: 'action-list-widget-v2',
    template: require('./action-list-widget-v2.component.html'),
    styles: [require('../../shared/list-widget-v2/list-widget-v2.css'), require('../action-list-v2/action-list-widget-v2.component.css')],
    providers: [ActionListWidgetV2Service, ThingListWidgetV2Service]
})
export class ActionListWidgetV2Component extends ListWidgetV2Component<ActionStatus> implements OnInit, AfterContentInit {

    @Input() searchFields: string[] = ['state', 'actionDefinition.name', 'actionDefinition.label', 'actionDefinition.description', 'actionDefinition.group'];

    @Input() addActionEnabled: boolean = true;

    @ContentChildren(COMPONENT_DEFINITION_REF) private columnComponents: QueryList<any>;

    @ContentChild(SearchFieldComponent) searchField: SearchFieldComponent;

    @ViewChild(ActionListWidgetReschedulingDialogComponent) reschedulingDialog: ActionListWidgetReschedulingDialogComponent;

    hidden: boolean;
    isActive: boolean = true;
    initCompleted: boolean;
    searchFieldId: string;
    searchFieldInputs: SearchFieldInput[];
    showAdd: boolean;
    thingId: string;
    showWorkOrders: boolean;
    writeWorkOrders: boolean;
    snackbarButtons: ActionSnackbarButton[];
    dividerIndexes: number[];
    workOrders: WorkOrder[];
    workOrderContext: WorkOrder;

    private actionStatuses: ActionStatus[];
    private defaultProperties: { [name: string]: PropertyInfo } = {
        icon: { label: '', path: 'actionDefinition.iconUrl', defaultFilter: null, defaultSorting: null },
        state: { label: 'statusProperty', path: 'state', defaultFilter: null, defaultSorting: null },
        "actionDefinition.label": { label: 'actionProperty', path: 'actionDefinition.label', defaultFilter: null, defaultSorting: null },
        startTimestamp: { label: 'startDateProperty', path: 'startTimestamp', defaultFilter: DatetimeFormatterPipe, defaultSorting: null }
    };
    private defaultSearchFieldInputs: SearchFieldInput[] = [
        { type: SearchFieldInputType.PROPERTY, property: 'actionDefinitionTypes', columnComponents: null },
        { type: SearchFieldInputType.PROPERTY, property: 'topics', columnComponents: null },
        { type: SearchFieldInputType.KEY, property: null, columnComponents: null },
        { type: SearchFieldInputType.ADVANCED, property: null, columnComponents: null },
        { type: SearchFieldInputType.CLEAR, property: null, columnComponents: null }
    ];

    constructor(
        @Inject(forwardRef(() => ActionListWidgetV2Service)) private actionListWidgetV2Service: ActionListWidgetV2Service,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => ActionContextService)) private actionContextService: ActionContextService,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => FieldService)) protected fieldService: FieldService,
        @Inject(forwardRef(() => ThingListWidgetV2Service)) private thingListWidgetService: ThingListWidgetV2Service,
        @Inject(forwardRef(() => AbstractExportContextService)) exportService: AbstractExportContextService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => StatisticService)) private statisticService: StatisticService
    ) {
        super(appService, authenticationService, exportService, null, dialog, snackbarService, null);
    }

    ngOnInit() {
        this.searchFieldId = 'action-list-query' + this.getNextId();
        this.checkIsMobile();
        this.checkIfHidden();
        this.readPermission = this.authenticationService.isCustomerUser() || this.authenticationService.isLocationUser()
            || ((this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) && !!this.contextService.getCurrentCustomer());
        this.showWorkOrders = this.authenticationService.hasPermission(Permissions.READ_WORK_ORDER);
        this.writeWorkOrders = this.authenticationService.hasPermission(Permissions.WRITE_WORK_ORDER);
        this.actionListWidgetV2Service.setActive(this.isActive);
        this.subscribeToPeriodField();
        this.preserveSelectedBetweenPages = true;
        this.initWorkOrders();
    }

    private initWorkOrders(): Promise<any> {
        this.workOrders = null;
        if (this.showWorkOrders && this.thingId) {
            return this.actionListWidgetV2Service.getRecursivelyAllWorkOrders(this.thingId, true).then(workOrders => {
                this.workOrders = workOrders;
            });
        }
        return Promise.resolve();
    }

    private checkIfHidden(): void {
        if (!this.thingContextService.getCurrentThing()) {
            if (this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) {
                this.hidden = !this.authenticationService.hasFeature('multipleThingAggregationForOrgPartner');
            } else {
                this.hidden = !this.authenticationService.hasFeature('multipleThingAggregationForCustomer');
            }
        } else {
            this.thingId = this.thingContextService.getCurrentThing().id;
            this.showAdd = true;
        }
    }

    ngAfterContentInit(): void {
        if (this.readPermission) {
            this.getDisplayedColumns([]);
            if (!this.sort) {
                this.sort = this.actionListWidgetV2Service.setDefaultSort(this.displayedColumns);
            }
            setTimeout(() => {      //waits for search field init
                this.getSearchFieldInputs();
                this.initCompleted = true;
            });
        }
    }

    private getSearchFieldInputs(): void {
        this.searchFieldInputs = this.searchField?.inputs || this.defaultSearchFieldInputs;
    }

    private getDisplayedColumns(actionStatuses: ActionStatus[]): void {
        const components = this.columnComponents.toArray().filter(col => !(col instanceof SearchFieldComponent));
        this.displayedColumns = this.actionListWidgetV2Service.getVisibleColumns(components, this.defaultProperties, 'ActionStatus');
        let iconValueMapReschedule = {
            "_default": {
                isFontAwesome: false,
                customIconClass: 'material-icons action-reschedule-icon text-default',
                customIconHtml: 'next_plan'
            }
        }
        if (actionStatuses?.some(a => a['showReschedule'])) {
            this.displayedColumns.push(
                CustomTableService.newIconColumn('reschedule', '', 'showReschedule', iconValueMapReschedule).withVisiblePath('showReschedule').withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } }).withColumnClass('no-print')
            );
        }
        if (this.clickOnRowBehaviour == ClickOnRowBehaviour.OPEN_CONTEXT_OBJECT) {
            let iconValueMapInfo = {
                "_default": {
                    isFontAwesome: false,
                    customIconClass: 'material-icons action-info-icon text-default',
                    customIconHtml: 'info'
                }
            }
            this.displayedColumns.push(
                CustomTableService.newIconColumn('info', '', 'showInfo', iconValueMapInfo).withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } }).withColumnClass('no-print')
            );
        }
        if (this.isActive && (!this.showWorkOrders || !actionStatuses.some(a => a.workOrderId)) || this.workOrderContext) {
            this.displayedColumns = this.displayedColumns.filter(c => !c.name.startsWith('workOrder'));
        } else if (this.workOrderContext && this.isActive) {
            this.displayedColumns.push(CustomTableService.newActionExecutionColumn(this.workOrderContext.status != WorkOrderStatus.EXECUTION).withSortField(this.isActive ? 'executionState' : null));
        } else {
            this.displayedColumns.push(
                CustomTableService.newPipedColumn('executionState', 'executionProperty', this.isActive ? 'executionState' : 'state', ActionExecutionStatePipe).withArgument('workOrder.status', true).withSortField(this.isActive ? 'executionState' : null)
            );
        }
    }

    private getActionList(): void {
        this.actionListWidgetV2Service.getPagedList(this.pageIndex, this.pageSize, this.sort, this.searchFields, this.advancedSearchBody, this.workOrderContext?.id).then(pagedList => {
            this.actionStatuses = pagedList.content;
            this.setIconsVisiblity(pagedList.content);
            this.updateRowClass(pagedList.content);
            this.getDisplayedColumns(pagedList.content);
            let things: Thing[] = pagedList.content.filter(as => as.thing).map(as => { return as.thing });
            this.thingListWidgetService.addTags(things, this.contextService.getTagObjects());
            this.updateElementList(pagedList, !this.isActive);
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getActionList();
    }

    private setIconsVisiblity(elements: ActionStatus[]): any {
        elements.forEach(el => {
            if (this.isActive && !el.workOrderId) {
                el['showReschedule'] = el.actionDefinition?.activationType == ActivationType.PERIOD && el.actionDefinition?.reschedulable ? this.localizationPipe.transform('Reschedule') : null;
            }
            el['showInfo'] = this.localizationPipe.transform('Info');
        });
        return elements;
    }

    execButtonAction(actionValue: ButtonActionValue): void {
        const actionStatus = this.actionStatuses[actionValue.index];
        if (actionValue.action == 'updateExecutionState') {
            const executionState = actionValue.value;
            if (executionState == ActionExecutionState.DISCARDED) {
                this.openDiscardDialog(actionStatus);
            } else if (executionState == ActionExecutionState.DONE_WITH_ISSUE) {
                this.openMarkAsDoneWithIssueDialog(actionStatus);
            } else if (executionState == ActionExecutionState.DONE) {
                if (actionStatus.executionState) {
                    this.openConfirmDialog(actionStatus);
                } else {
                    this.updateActionState({ executionState: actionValue.value }, actionStatus);
                }
            } else {
                this.updateActionState({ executionState: actionValue.value }, actionStatus);
            }
        } else if (actionValue.action == 'reschedule') {
            this.reschedulingDialog.open(actionStatus);
        } else if (actionValue.action == 'info') {
            this.openActionInfoDialog(actionStatus);
        } else if (actionValue.action == 'openWorkOrder') {
            let workOrder = actionValue.value;
            if (this.isActive) { // retrieve the enriched
                workOrder = this.workOrders.find(w => w.id == actionStatus.workOrderId);
            } else { // sets the id to filter actions
                workOrder.id = actionStatus.workOrderId;
            }
            this.setWorkOrderContext(workOrder);
        }
    }

    private openConfirmDialog(actionStatus: ActionStatus): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "markAsDoneActionMessage",
            message: "markAsDoneActionInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.updateActionState({ executionState: ActionExecutionState.DONE }, actionStatus);
            } else {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    private updateActionState(body: any, actionStatus: ActionStatus): void {
        this.userActionStatusService.patchActionStatus(actionStatus, body).then(() => {
            this.refreshElementList();
            this.actionContextService.updateRefreshEventSubject(true);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    goToDetail(status: ActionStatus): void {
        if (status && status.thingId && this.clickOnRowBehaviour == ClickOnRowBehaviour.OPEN_CONTEXT_OBJECT) {
            this.navigationService.goToThingDetailPage(status.thingId);
        } else if (status) {
            this.openActionInfoDialog(status);
        }
    }

    private updateRowClass(elements: ActionStatus[]): ActionStatus[] {
        if (elements?.length > 0) {
            elements.forEach(as => {
                as['rowClass'] = as.actionDefinition?.priority;
            });
        }
        return elements;
    }

    protected subscribeToExportServices(): void {
        // do nothing, export not supported
    }

    rescheduleAction(rescheduleBody: any): void {
        const actionStatusId = rescheduleBody.actionStatusId;
        delete rescheduleBody.actionStatusId;
        this.updateActionState(rescheduleBody, this.actionStatuses.find(el => el.id == actionStatusId));
    }

    private openActionInfoDialog(actionStatus: ActionStatus): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.width = '600px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = { actionStatus: actionStatus, isHistorical: !this.isActive };
        dialogConfig.viewContainerRef = this.vcRef;
        this.dialog.open(ActionInfoDialogV2Component, dialogConfig).afterClosed().pipe(take(1)).subscribe(refresh => {
            if (refresh) {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    changeStatus(newStatus: boolean): void {
        this.isActive = newStatus;
        this.actionListWidgetV2Service.setActive(this.isActive);
        this.sort = [];
        this.elements = [];
        this.selectedElements = [];
        this.allElementsSelected = false;
        this.loaded = false;
        this.pageIndex = 0;
        this.initCompleted = false;
        setTimeout(() => {
            this.initCompleted = true;
        }, 10);
    }

    addAction(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '75%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            thing: this.thingContextService.getCurrentThing(),
            actionListWidgetV2Service: this.actionListWidgetV2Service,
            isActive: this.isActive,
            displayedColumns: this.getAddActionDialogDisplayColumns(),
            workOrder: this.workOrderContext
        }
        this.dialog.open(AddActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(refresh => {
            if (refresh) {
                this.refreshElementList();
                if (this.workOrderContext) {
                    this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
                }
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    private getAddActionDialogDisplayColumns(): CustomTableColumn[] {
        const offset = "actionDefinition.".length;
        return _.cloneDeep(this.displayedColumns).filter(c => c.name.startsWith("actionDefinition.")).map(c => {
            c.path = c.path.substring(offset);
            c.sortField = c.path;
            return c;
        });
    }

    private openDiscardDialog(actionStatus?: ActionStatus): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.panelClass = "discard-action-confirm-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = { actionStatus: actionStatus };
        this.dialog.open(DiscardActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(body => {
            if (body) {
                if (actionStatus) {
                    if (actionStatus.workOrderId) {
                        body.executionState = ActionExecutionState.DISCARDED;
                    } else {
                        body.state = ActionState.DISCARDED;
                    }
                    this.updateActionState(body, actionStatus);
                } else {
                    body.elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
                    this.actionListWidgetV2Service.bulkMarkAsDiscarded(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, body, this.allElementsSelected).then(resp => {
                        this.clearSelectedElements();
                        this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
                        this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions marked as discarded')}`);
                    }).catch(err => this.error = ErrorUtility.getMessage(err));
                }
            } else if (actionStatus) {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    private openMarkAsDoneWithIssueDialog(actionStatus: ActionStatus): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.panelClass = "mark-as-done-with-issue-action-confirm-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = { actionStatus: actionStatus };
        this.dialog.open(MarkAsDoneWithIssueActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(body => {
            if (body) {
                if (actionStatus.workOrderId) {
                    body.executionState = ActionExecutionState.DONE_WITH_ISSUE;
                } else {
                    body.state = ActionState.DONE;
                }
                this.updateActionState(body, actionStatus);
            } else {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    createWorkOrder(withSelection?: boolean): Promise<any> {
        this.loaded = false;
        let actionStatusIds = [];
        if (withSelection && !this.allElementsSelected) {
            actionStatusIds = this.selectedElements.map(a => a.id);
        }
        return this.actionListWidgetV2Service.createWorkOrder(actionStatusIds, this.thingId)
            .then(workOrder => {
                let bulkAddPromise: Promise<any> = Promise.resolve();
                if (!actionStatusIds.length) {
                    bulkAddPromise = this.actionListWidgetV2Service.bulkAddToWorkOrder(this.searchFields, this.advancedSearchBody, workOrder.id, [], true);
                }
                return bulkAddPromise.then(() => {
                    this.initWorkOrders();
                    if (!withSelection) { // no context change
                        this.actionListWidgetV2Service.getWorkOrder(workOrder.id).then(wo => this.setWorkOrderContext(wo));
                    }
                }).catch(err => this.error = ErrorUtility.getMessage(err));;
            }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    setWorkOrderContext(workOrder: WorkOrder): void {
        this.workOrderContext = workOrder;
        if (workOrder) {
            this.isActive = !this.isHistorical(workOrder);
            this.actionListWidgetV2Service.setActive(this.isActive);
        } else {
            this.initWorkOrders();
        }
        this.clearSelectedElements();
        this.refreshList();
    }

    isHistorical(workOrder: WorkOrder): boolean {
        return workOrder.status == WorkOrderStatus.COMPLETED || workOrder.status == WorkOrderStatus.DISCARDED;
    }

    // when snackbar will be extended to all elements move this in abstract class
    override updateSelectedElements(elements: ActionStatus[]): void {
        super.updateSelectedElements(elements);
        this.updateSnackbarButtons();
    }

    override clearSelectedElements(): void {
        super.clearSelectedElements();
        this.updateSnackbarButtons();
    }

    override setAllElementsSelected(): void {
        super.setAllElementsSelected();
        this.updateSnackbarButtons();
    }

    override changeSort(sort: Sort): void {
        super.changeSort(sort);
        this.updateSnackbarButtons();
    }

    override changePage(pageEvent: PageEvent): void {
        super.changePage(pageEvent);
        this.updateSnackbarButtons();
    }

    private updateSnackbarButtons(): void {
        if (this.selectedElements.length) {
            const allElementsCount = this.allElementsSelected ? this.length : this.selectedElements.length;
            let dividerPosition = 0;
            this.snackbarButtons = [
                { type: ActionSnackbarButtonType.SELECTED_ELEMENTS, label: allElementsCount + '', event: 'unselect' }
            ];
            if (!this.allElementsSelected) {
                this.snackbarButtons.push({ type: ActionSnackbarButtonType.SIMPLE, label: 'selectAllButton', event: 'selectAll', icon: 'select_all' });
                dividerPosition++;
            }
            let showDivider = false;
            if (this.writeWorkOrders) {
                if (this.workOrderContext) {
                    if (this.workOrderContext.status == WorkOrderStatus.EXECUTION) {
                        this.snackbarButtons.push({
                            type: ActionSnackbarButtonType.MENU, label: 'executeButton', icon: 'task_alt', menuButtons: [
                                { label: 'markAsDiscardedProperty', event: 'markAsDiscarded' }
                            ]
                        });
                        showDivider = true;
                    }
                    if (this.workOrderContext.status == WorkOrderStatus.DRAFT) {
                        this.snackbarButtons.push({ type: ActionSnackbarButtonType.SIMPLE, label: 'removeFromWorkOrderProperty', event: 'removeFromWorkOrder', icon: 'playlist_remove' });
                        showDivider = true;
                    }
                    if (this.workOrderContext.status == WorkOrderStatus.EXECUTION && (this.allElementsSelected || this.selectedElements.some(a => a.workOrderNative))) {
                        this.snackbarButtons.push({ type: ActionSnackbarButtonType.SIMPLE, label: 'deleteButton', event: 'deleteWorkOrderNative', icon: 'delete' });
                        showDivider = true;
                    }
                } else { // !workOrderContext
                    if (this.allElementsSelected) {
                        this.existsAnyActionWithoutWorkOrder().then(exists => {
                            if (exists) {
                                this.snackbarButtons.push({ type: ActionSnackbarButtonType.SIMPLE, label: 'addToWorkOrderProperty', event: 'addToWorkOrder', icon: 'playlist_add' });
                                this.dividerIndexes = [dividerPosition];
                            }
                        });

                    } else if (this.selectedElements.some(a => !a.workOrderId)) {
                        this.snackbarButtons.push({ type: ActionSnackbarButtonType.SIMPLE, label: 'addToWorkOrderProperty', event: 'addToWorkOrder', icon: 'playlist_add' });
                        showDivider = true;
                    }
                }
            }
            this.dividerIndexes = showDivider ? [dividerPosition] : null;
        } else {
            this.snackbarButtons = null;
            this.dividerIndexes = null;
        }
    }

    private existsAnyActionWithoutWorkOrder(): Promise<boolean> {
        let params = new HttpParams();
        params = params.set('resource', 'action');
        params = params.set('aggregation', 'COUNT');
        params = params.set('thingId', this.thingId);
        params = params.set('workOrderId', `exists;false`);
        if (this.advancedSearchBody) {
            if (this.advancedSearchBody.key) {
                params = params.set('searchText', "*" + this.advancedSearchBody.key + "*");
                this.searchFields.forEach(field => params = params.append('searchField', field));
            }
            if (this.advancedSearchBody.topics?.length) {
                this.advancedSearchBody.topics.forEach(topic => params = params.append('topic', topic));
            }
            if (this.advancedSearchBody.actionDefinitions?.length) {
                this.advancedSearchBody.actionDefinitions.forEach(actionDefinitionId => params = params.append('actionDefinitionId', actionDefinitionId));
            }
            if (this.advancedSearchBody.priorities?.length) {
                this.advancedSearchBody.priorities.forEach(priority => params = params.append('priority', priority));
            }
            if (this.advancedSearchBody.statuses?.length) {
                this.advancedSearchBody.statuses.forEach(state => params = params.append('state', state));
            }
            if (this.advancedSearchBody.selectedThingIds?.length) {
                this.advancedSearchBody.selectedThingIds.forEach(id => params = params.append('selectedThingId', id));
            }
            if (this.advancedSearchBody.startTimestamp) {
                params = params.set('minStartTimestamp', this.advancedSearchBody.startTimestamp);
            }
            if (this.advancedSearchBody.endTimestamp) {
                params = params.set('maxStartTimestamp', this.advancedSearchBody.endTimestamp);
            }
            if (this.advancedSearchBody.actionDefinitionTypes?.length) {
                this.advancedSearchBody.actionDefinitionTypes.forEach(type => params = params.append('actionDefinitionType', type));
            }
        }
        return this.statisticService.getStatisticPromise(null, params).then(results => {
            const count = results[0];
            return !!count?.value;
        });
    }

    doAction(event: string): void {
        switch (event) {
            case 'unselect':
                this.clearSelectedElements();
                break;
            case 'selectAll':
                this.setAllElementsSelected();
                break;
            case 'markAsDiscarded':
                this.bulkMarkAsDiscarded();
                break;
            case 'addToWorkOrder':
                this.openWorkOrderSelectionDialog();
                break;
            case 'removeFromWorkOrder':
                this.removeFromWorkOrder();
                break;
            case 'deleteWorkOrderNative':
                this.bulkDeleteWorkOrderNative();
                break;
        }
    }


    private openWorkOrderSelectionDialog(): void {
        const selectableWorkOrders = this.workOrders?.filter(w => WorkOrderStatus.DRAFT == w.status || WorkOrderStatus.EXECUTION == w.status);
        if (selectableWorkOrders?.length) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.autoFocus = false;
            dialogConfig.maxWidth = '320px';
            dialogConfig.data = {
                workOrders: selectableWorkOrders
            }
            this.dialog.open(AddToWorkOrderDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
                if (result) {
                    if (result.new) {
                        this.createWorkOrder(true).then(() => {
                            this.clearSelectedElements();
                            this.refreshList();
                        });
                    } else {
                        const elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
                        this.actionListWidgetV2Service.bulkAddToWorkOrder(this.searchFields, this.advancedSearchBody, result.workOrder.id,
                            elementIds, this.allElementsSelected).then(resp => {
                                this.clearSelectedElements();
                                this.initWorkOrders();
                                this.refreshList();
                                this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions added to work order')}`);
                            }).catch(err => this.error = ErrorUtility.getMessage(err));
                    }
                }
            });
        } else {
            this.createWorkOrder(true).then(() => {
                this.clearSelectedElements();
                this.refreshList();
            });
        }
    }

    private removeFromWorkOrder(): void {
        const elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
        this.actionListWidgetV2Service.bulkRemoveFromWorkOrder(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, elementIds, this.allElementsSelected).then(resp => {
            this.clearSelectedElements();
            this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
            this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions removed from work order')}`);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    editWorkOrder(statusOnly: boolean = false): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '420px';
        dialogConfig.data = {
            workOrder: this.workOrderContext,
            statusOnly: statusOnly
        }
        this.dialog.open(WorkOrderEditDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(body => {
            if (body) {
                this.actionListWidgetV2Service.updateWorkOrder(this.workOrderContext.id, body).then(workOrder => {
                    if (this.isHistorical(workOrder)) {
                        this.setWorkOrderContext(workOrder);
                    } else {
                        this.actionListWidgetV2Service.getWorkOrder(workOrder.id).then(wo => this.setWorkOrderContext(wo));
                    }
                }).catch(err => this.error = ErrorUtility.getMessage(err));
            }
        });
    }

    openWorkOrderHistory(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '75%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            thingId: this.thingContextService.getCurrentThing().id,
            actionListWidgetV2Service: this.actionListWidgetV2Service,
        }
        this.dialog.open(WorkOrderListDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(historicalWorkOrder => {
            if (historicalWorkOrder) {
                this.setWorkOrderContext(historicalWorkOrder);
            }
        });
    }

    private bulkMarkAsDiscarded(): void {
        this.openDiscardDialog();
    }

    private bulkDeleteWorkOrderNative(): void {
        const elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
        this.actionListWidgetV2Service.bulkDeleteWorkOrderNative(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, elementIds, this.allElementsSelected).then(resp => {
            this.clearSelectedElements();
            this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
            this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions deleted')}`);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

}